import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useSubscriptionCheck from '../../common/SubscriptionCheck';
import InvoiceSelect from '../../../components/form/InvoiceSelect';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { CREATE_PURCHASE_CREDIT_NOTE, CREATE_SALE_CREDIT_NOTE } from '../../../constants/eInvoicePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';

function CreditNoteListPageActions({ actions, isPurchase = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const { checkSubscription, SubscriptionModal } = useSubscriptionCheck();
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const { eInvoiceForm } = useTranslatedMessages();
  const { document_list } = eInvoiceForm;
  const { create_credit_note_dialog } = document_list;

  const creditNoteButtonLabel = isPurchase
    ? document_list.create_self_billed_credit_note
    : document_list.create_credit_note;

  const createPermission = isPurchase ? CREATE_PURCHASE_CREDIT_NOTE : CREATE_SALE_CREDIT_NOTE;
  const canCreate = hasEInvoicePermission(createPermission);

  const handleMenuOpen = (event) => {
    if (!canCreate || !checkSubscription()) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedInvoice(null);
  };

  const handleContinue = () => {
    if (!canCreate) {
      return;
    }
    if (selectedInvoice) {
      navigate(isPurchase ? '/purchases/documents/credit-notes/create' : '/sales/documents/credit-notes/create', {
        state: {
          source: 'invoice',
          invoiceData: selectedInvoice,
        },
      });
    }
  };

  const handleCreateCustomCreditNote = () => {
    if (!canCreate) {
      return;
    }
    handleMenuClose();
    navigate(isPurchase ? '/purchases/documents/credit-notes/create' : '/sales/documents/credit-notes/create');
  };

  return (
    <>
      {actions.map((action, index) => (
        <PermissionDeniedWrapper key={index} show={!canCreate} title={accessDeniedMessage}>
          <Button
            id="create-credit-note"
            variant="contained"
            color="primary"
            onClick={handleMenuOpen}
            disabled={!canCreate}
          >
            {creditNoteButtonLabel}
          </Button>
        </PermissionDeniedWrapper>
      ))}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setOpen(true);
          }}
        >
          {document_list.create_linked_credit_note}
        </MenuItem>

        <MenuItem onClick={handleCreateCustomCreditNote}>{document_list.create_custom_credit_note}</MenuItem>
      </Menu>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{create_credit_note_dialog.title}</DialogTitle>
        <Divider />
        <DialogContent>{create_credit_note_dialog.description}</DialogContent>
        <DialogContent>
          <InvoiceSelect name="select-invoice" onSelect={setSelectedInvoice} isPurchase={isPurchase} />
        </DialogContent>
        <DialogActions>
          <Button id="credit-note-cancel" onClick={handleClose} variant="outlined">
            {create_credit_note_dialog.cancel}
          </Button>
          <Button id="credit-note-continue" onClick={handleContinue} variant="contained" disabled={!selectedInvoice}>
            {create_credit_note_dialog.continue}
          </Button>
        </DialogActions>
      </Dialog>
      <SubscriptionModal />
    </>
  );
}

export default CreditNoteListPageActions;
